/**
 * OnboardCustomerModal.jsx
 *
 * Creates a new customer account, organisation, and first vehicle in one step
 * via the PUBLIC POST /onboard endpoint.
 *
 * IMPORTANT: This component intentionally uses its own axios instance that does NOT
 * attach the admin's Bearer token — /onboard is a public endpoint.
 *
 * On success the modal switches to a one-time credentials confirmation view.
 * The access_token / refresh_token from the response are deliberately NOT stored
 * anywhere — storing them would replace the admin's own session.
 */

import React, { useState, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCopy,
  ClipboardCheck,
  UserPlus,
  Car,
  MapPin,
  Info,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { API_BASE_URL, extractApiError } from "@/lib/api";
import { useOnboardVehicleOptions } from "@/hooks/useOnboardVehicleOptions";
import CreatableDropdownInput from "@/components/shared/CreatableDropdownInput";

// ── Public (no-auth) axios instance ──────────────────────────────────────────
// /onboard is a public endpoint — no Authorization header should be sent.
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 1989 }, (_, i) =>
  String(CURRENT_YEAR - i)
);

// ── Field constants ───────────────────────────────────────────────────────────
const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  phone: "",
  state: "",
  city: "",
  district: "",
  pincode: "",
  registration_number: "",
  vin: "",
  mac_address: "",
  make: "",
  model: "",
  year: "",
  vehicle_type: "",
};

// Fields whose absence should block submission
const REQUIRED_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "password",
  "state",
  "city",
  "district",
  "pincode",
  "registration_number",
  "vin",
  "mac_address",
];

// ── Small shared helpers ──────────────────────────────────────────────────────

function FieldError({ error }) {
  if (!error) return null;
  return (
    <p className="mt-1 text-xs font-medium text-rose-600" role="alert">
      {error}
    </p>
  );
}

function FieldLabel({ children, required, optional }) {
  return (
    <Label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
      {children}
      {required && <span className="ml-0.5 text-rose-500">*</span>}
      {optional && (
        <span className="ml-1 normal-case font-normal text-slate-400">
          (optional)
        </span>
      )}
    </Label>
  );
}

function SectionHeading({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900">
        <Icon size={15} className="text-white" />
      </div>
      <div>
        <p className="text-sm font-bold text-slate-800">{title}</p>
        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      </div>
    </div>
  );
}

// ── Copy-to-clipboard button ──────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(String(text ?? "")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
      title="Copy to clipboard"
    >
      {copied ? (
        <ClipboardCheck size={14} className="text-emerald-500" />
      ) : (
        <ClipboardCopy size={14} />
      )}
    </button>
  );
}

// ── Single credential display row ─────────────────────────────────────────────

function CredentialRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          {label}
        </p>
        <p className="mt-0.5 break-all font-mono text-sm font-semibold leading-snug text-slate-800">
          {value ?? "—"}
        </p>
      </div>
      {value != null && <CopyButton text={String(value)} />}
    </div>
  );
}

// ── Confirmation view (success) ───────────────────────────────────────────────

function ConfirmationView({ result, onDone }) {
  return (
    <div className="p-8 space-y-6">
      {/* Success header */}
      <div className="flex items-start gap-4">
        <div className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
          <CheckCircle2 size={24} className="text-emerald-600" />
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-900">Customer Onboarded</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Account, organisation, and first vehicle created successfully.
          </p>
        </div>
      </div>

      {/* One-time credentials warning */}
      <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertTriangle
          size={16}
          className="mt-0.5 shrink-0 text-amber-600"
        />
        <p className="text-sm font-medium text-amber-700">
          These credentials are shown{" "}
          <span className="font-black">only once</span>. Copy and store them
          securely before closing this dialog.
        </p>
      </div>

      {/* Credentials grid */}
      <div className="space-y-2.5">
        {/* <CredentialRow label="Org ID" value={result.org_id} />
        <CredentialRow label="User ID" value={result.user_id} />
        <CredentialRow label="Vehicle ID" value={result.vehicle_id} />
        <CredentialRow label="Device ID" value={result.device_id} /> */}
        <CredentialRow label="EMQX Username" value={result.emqx_username} />
        <CredentialRow label="EMQX Password" value={result.emqx_password} />
      </div>

      {/* Temporary password reminder */}
      <div className="flex gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <Info size={16} className="mt-0.5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-600">
          <span className="font-bold text-slate-700">Temporary password:</span>{" "}
          Remember to communicate the customer's temporary account password
          through your secure out-of-band channel (email, SMS, etc.). No
          server-side must-change-password enforcement is applied.
        </p>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={onDone}
          className="h-11 rounded-xl bg-slate-900 px-8 font-bold text-white hover:bg-slate-800"
        >
          Done
        </Button>
      </div>
    </div>
  );
}

// ── Onboard form ──────────────────────────────────────────────────────────────

function OnboardForm({
  formData,
  fieldErrors,
  submitError,
  isSubmitting,
  setField,
  handleSubmit,
  handleClose,
  makes = [],
  models = [],
  vehicleTypes = [],
  optionsLoading = false,
}) {
  const makeOptions = Array.isArray(makes) ? makes : [];
  const modelOptions = Array.isArray(models) ? models : [];
  const vehicleTypeOptions = Array.isArray(vehicleTypes) ? vehicleTypes : [];
  const inputCls = (field) =>
    `bg-white border-slate-200 h-11 ${fieldErrors[field] ? "border-rose-400 focus-visible:ring-rose-300" : ""}`;

  return (
    <>
      <DialogHeader className="border-b border-slate-100 bg-white px-8 py-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900">
            <UserPlus size={18} className="text-white" />
          </div>
          <div>
            <DialogTitle className="text-xl font-black text-slate-900">
              Onboard New Customer
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Creates a customer account, organisation, and first vehicle in one step.
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <form onSubmit={handleSubmit} noValidate className="p-8 space-y-8">
        {/* ── Section 1: Account Details ── */}
        <section>
          <SectionHeading
            icon={UserPlus}
            title="Account Details"
            subtitle="Customer login credentials and contact info"
          />

          <div className="space-y-4">
            {/* First + last name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel required>First Name</FieldLabel>
                <Input
                  placeholder="John"
                  className={inputCls("first_name")}
                  value={formData.first_name}
                  onChange={(e) => setField("first_name", e.target.value)}
                />
                <FieldError error={fieldErrors.first_name} />
              </div>
              <div className="space-y-1.5">
                <FieldLabel required>Last Name</FieldLabel>
                <Input
                  placeholder="Doe"
                  className={inputCls("last_name")}
                  value={formData.last_name}
                  onChange={(e) => setField("last_name", e.target.value)}
                />
                <FieldError error={fieldErrors.last_name} />
              </div>
            </div>

            {/* Email + phone */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel required>Email</FieldLabel>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  className={inputCls("email")}
                  value={formData.email}
                  onChange={(e) => setField("email", e.target.value)}
                />
                <FieldError error={fieldErrors.email} />
              </div>
              <div className="space-y-1.5">
                <FieldLabel optional>Phone</FieldLabel>
                <Input
                  type="tel"
                  placeholder="+91 98765 43210"
                  className={inputCls("phone")}
                  value={formData.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                />
                <FieldError error={fieldErrors.phone} />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <FieldLabel required>Password</FieldLabel>
              <Input
                type="password"
                placeholder="Min. 8 characters"
                className={inputCls("password")}
                value={formData.password}
                onChange={(e) => setField("password", e.target.value)}
              />
              <FieldError error={fieldErrors.password} />
              {!fieldErrors.password && (
                <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <KeyRound size={11} />
                  Temporary password — communicate it to the customer via your
                  secure channel after onboarding.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ── Section 2: Address ── */}
        <section className="border-t border-slate-200 pt-7">
          <SectionHeading
            icon={MapPin}
            title="Address"
            subtitle="Customer's registered address"
          />

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel required>State</FieldLabel>
                <Input
                  placeholder="Maharashtra"
                  className={inputCls("state")}
                  value={formData.state}
                  onChange={(e) => setField("state", e.target.value)}
                />
                <FieldError error={fieldErrors.state} />
              </div>
              <div className="space-y-1.5">
                <FieldLabel required>City</FieldLabel>
                <Input
                  placeholder="Mumbai"
                  className={inputCls("city")}
                  value={formData.city}
                  onChange={(e) => setField("city", e.target.value)}
                />
                <FieldError error={fieldErrors.city} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel required>District</FieldLabel>
                <Input
                  placeholder="Mumbai Suburban"
                  className={inputCls("district")}
                  value={formData.district}
                  onChange={(e) => setField("district", e.target.value)}
                />
                <FieldError error={fieldErrors.district} />
              </div>
              <div className="space-y-1.5">
                <FieldLabel required>Pincode</FieldLabel>
                <Input
                  placeholder="400001"
                  className={`font-mono ${inputCls("pincode")}`}
                  value={formData.pincode}
                  onChange={(e) => setField("pincode", e.target.value)}
                />
                <FieldError error={fieldErrors.pincode} />
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 3: Vehicle ── */}
        <section className="border-t border-slate-200 pt-7">
          <SectionHeading
            icon={Car}
            title="Vehicle Details"
            subtitle="First vehicle and IoT tracking device"
          />

          <div className="space-y-4">
            {/* Reg number + VIN */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel required>Registration Number</FieldLabel>
                <Input
                  placeholder="MH-01-AB-1234"
                  className={`font-mono font-bold ${inputCls("registration_number")}`}
                  value={formData.registration_number}
                  onChange={(e) =>
                    setField("registration_number", e.target.value)
                  }
                />
                <FieldError error={fieldErrors.registration_number} />
              </div>
              <div className="space-y-1.5">
                <FieldLabel required>VIN</FieldLabel>
                <Input
                  placeholder="e.g. MA1AB2CD3EF456789"
                  className={`font-mono uppercase ${inputCls("vin")}`}
                  value={formData.vin}
                  // Auto-uppercase as the admin types — backend also uppercases
                  onChange={(e) =>
                    setField("vin", e.target.value.toUpperCase())
                  }
                />
                <FieldError error={fieldErrors.vin} />
              </div>
            </div>

            {/* MAC address (full width) */}
            <div className="space-y-1.5">
              <FieldLabel required>MAC Address</FieldLabel>
              <Input
                placeholder=""
                className={`font-mono ${inputCls("mac_address")}`}
                value={formData.mac_address}
                onChange={(e) => setField("mac_address", e.target.value)}
              />
              <FieldError error={fieldErrors.mac_address} />
            </div>

            {/* Make + model */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel optional>Make</FieldLabel>
                <CreatableDropdownInput
                  id="customer-make"
                  placeholder="Select or type make"
                  value={formData.make}
                  onChange={(value) => setField("make", value)}
                  options={makeOptions}
                  loading={optionsLoading}
                  noOptionsText="No makes available. Type a custom make."
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel optional>Model</FieldLabel>
                <CreatableDropdownInput
                  id="customer-model"
                  placeholder="Select or type model"
                  value={formData.model}
                  onChange={(value) => setField("model", value)}
                  options={modelOptions}
                  loading={optionsLoading}
                  noOptionsText="No models available. Type a custom model."
                />
              </div>
            </div>

            {/* Year + vehicle type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <FieldLabel optional>Year</FieldLabel>
                <Select
                  value={formData.year}
                  onValueChange={(v) => setField("year", v)}
                >
                  <SelectTrigger className="bg-white border-slate-200 h-11">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {YEARS.map((y) => (
                      <SelectItem key={y} value={y}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <FieldLabel optional>Vehicle Type</FieldLabel>
                <CreatableDropdownInput
                  id="customer-vehicle-type"
                  placeholder="Select or type vehicle type"
                  value={formData.vehicle_type}
                  onChange={(value) => setField("vehicle_type", value)}
                  options={vehicleTypeOptions}
                  loading={optionsLoading}
                  noOptionsText="No vehicle types available. Type a custom vehicle type."
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Top-level error banner ── */}
        {submitError && (
          <div className="flex gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <AlertTriangle
              size={16}
              className="mt-0.5 shrink-0 text-rose-500"
            />
            <p className="text-sm text-rose-700">{submitError}</p>
          </div>
        )}

        <DialogFooter className="gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting}
            className="font-bold text-slate-500"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="h-11 rounded-xl bg-slate-900 px-8 font-bold text-white hover:bg-slate-800"
          >
            {isSubmitting ? "Creating…" : "Complete Onboarding"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function OnboardCustomerModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    makes,
    models,
    vehicleTypes,
    loading: optionsLoading,
  } = useOnboardVehicleOptions({ enabled: isOpen });

  function setField(name, value) {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear per-field error as the user corrects it
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  function validateForm() {
    const errors = {};

    // Blank / whitespace check on all required fields
    for (const field of REQUIRED_FIELDS) {
      if (!formData[field]?.trim()) {
        errors[field] = "This field is required.";
      }
    }

    // Password minimum length
    if (!errors.password && formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }

    // Basic email format check
    if (
      !errors.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      errors.email = "Please enter a valid email address.";
    }

    return errors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError("");
    setFieldErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Build payload — only include optional fields when non-empty
    const payload = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim(),
      password: formData.password,
      state: formData.state.trim(),
      city: formData.city.trim(),
      district: formData.district.trim(),
      pincode: formData.pincode.trim(),
      registration_number: formData.registration_number.trim(),
      // VIN sent uppercased; backend does this too but we're explicit
      vin: formData.vin.trim().toUpperCase(),
      mac_address: formData.mac_address.trim(),
      ...(formData.phone?.trim() ? { phone: formData.phone.trim() } : {}),
      ...(formData.make ? { make: formData.make } : {}),
      ...(formData.model?.trim() ? { model: formData.model.trim() } : {}),
      ...(formData.year ? { year: Number(formData.year) } : {}),
      ...(formData.vehicle_type ? { vehicle_type: formData.vehicle_type } : {}),
    };

    setIsSubmitting(true);
    try {
      const { data } = await publicApi.post("/onboard", payload);
      // Unwrap standard { data: { ... } } or bare envelope
      const resp = data?.data || data;
      // Notify parent (e.g. to refresh customer list) — fire-and-forget
      onSuccess?.(resp);
      toast.success("Onboarded successfully");
      handleClose();
    } catch (err) {
      const status = err?.response?.status;
      const { message, details } = extractApiError(err);

      if (status === 422 && Array.isArray(details?.fields)) {
        // Map per-field validation errors onto their inputs
        const fieldErrs = {};
        details.fields.forEach(({ field, message: msg }) => {
          fieldErrs[field] = msg;
        });
        setFieldErrors(fieldErrs);
        setSubmitError("Please fix the highlighted fields below.");
      } else {
        // 409 → exact message from backend (duplicate email / device / VIN)
        // Any other error → same
        setSubmitError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    // Reset all state when closing
    setFormData(EMPTY_FORM);
    setFieldErrors({});
    setSubmitError("");
    onClose();
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl rounded-[24px] border-0 shadow-2xl p-0 overflow-hidden bg-slate-50 max-h-[90vh] overflow-y-auto">
        <OnboardForm
          formData={formData}
          fieldErrors={fieldErrors}
          submitError={submitError}
          isSubmitting={isSubmitting}
          setField={setField}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          makes={makes}
          models={models}
          vehicleTypes={vehicleTypes}
          optionsLoading={optionsLoading}
        />
      </DialogContent>
    </Dialog>
  );
}
